import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { flatten } from "lodash";
import { Button, Col, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AgGridReact } from "ag-grid-react";
import { fetchMatchedTransactions } from "../../../../redux/reducers/MatchedTransactions";
import { IDatasource } from "ag-grid-community";
import { TableFilterConfig } from "src/docuclipper/DocuclipperTypes";
import { fetchTls } from "src/redux/reducers/TransactionManager";
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "../../../../redux";
import { datefilterParamsYYYYMMDD } from "../AnalyzeTransactionsTable";
import ExternalFilter from "src/views/Docuclipper/ExternalFilter";
import { useHasQbo } from "src/utils/utils";
import type { SankeyData } from "./FlowOfFundsChart";

export interface FlowOfFundsFilterState {
  selectedCategory?: string;
  selectedAccount?: string;
  tx1Account?: string;
  tx2Account?: string;
  linkFilterSource?: string;
  linkFilterDest?: string;
}

const externalFilterColumns = [
  { field: "date", label: "Date", filter: true, filterType: "date", defaultFilterType: "equals" },
  { field: "description", label: "Description", filter: true, filterType: "text", defaultFilterType: "contains" },
  { field: "amount", label: "Amount", filter: true, filterType: "number", defaultFilterType: "equals" },
];

const columnDefs = [
  { field: "account" },
  { field: "date", filter: "agDateColumnFilter", filterParams: datefilterParamsYYYYMMDD, sortable: true },
  { field: "description", filter: "agTextColumnFilter", filterParams: {} },
  { field: "amount", filter: "agNumberColumnFilter", sortable: true, filterParams: {} },
  { field: "category", filter: "agSetColumnFilter" },
];

const norm = (c: string | null | undefined) =>
  (c == null || c === "" ? "Uncategorized" : c);

/** Table component that subscribes to TransactionManager - isolated from chart re-renders */
export function FlowOfFundsTable({
  filter,
  data2,
  onClearFilters,
  gridApiRef,
}: {
  filter: FlowOfFundsFilterState;
  data2: SankeyData;
  onClearFilters: () => void;
  gridApiRef: React.MutableRefObject<any>;
}) {
  const dispatch = useDispatch();
  const ref = useRef<any>(null);
  const {
    selectedDocumentId,
    tls,
    selectedChunk,
    accountChunkLabels,
    pagination,
    refreshKey,
  } = useSelector((state: ReduxState) => state.TransactionManager);

  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel
  );
  const { job } = useSelector((state: ReduxState) => state.JobData);
  const { matches } = useSelector(
    (state: ReduxState) => state.MatchedTransactions
  );
  const hasQbo = useHasQbo();
  const { documents } = tls;

  const [isGridReady, setIsGridReady] = useState(false);

  // Use refs for values that CHANGE when fetchTls completes - including them in
  // the datasource effect deps would cause: fetch completes -> store updates ->
  // effect runs -> setDatasource -> grid refetches -> infinite loop
  const latestForFetch = useRef({
    accountChunkLabels,
    documents,
    hasQbo,
    job,
  });
  latestForFetch.current = {
    accountChunkLabels,
    documents,
    hasQbo,
    job,
  };

  const matchedIds = useMemo(
    () =>
      flatten(
        (matches || [])
          .filter(
            (x) =>
              x?.match?.status === "approved" && Array.isArray(x?.transactions)
          )
          .map((x) => x.transactions)
      )
        .filter((x) => x && x.id)
        .map((x) => x.id),
    [matches]
  );

  useEffect(() => {
    if (matches.length === 0) {
      dispatch(fetchMatchedTransactions());
    }
  }, [matches.length, dispatch]);

  useEffect(() => {
    if (gridApiRef?.current && isGridReady) {
      gridApiRef.current.onFilterChanged();
    }
  }, [
    filter.selectedCategory,
    filter.selectedAccount,
    filter.tx1Account,
    filter.tx2Account,
    filter.linkFilterSource,
    filter.linkFilterDest,
    isGridReady,
    gridApiRef,
  ]);

  useEffect(() => {
    if (!gridApiRef?.current || !isGridReady) return;

    const dataSource: IDatasource = {
      rowCount: undefined,
      getRows: (params) => {
        const convertFilterModel = (): TableFilterConfig[] => {
          return Object.entries(params.filterModel || {}).map(
            ([field, filterModel]: [string, any]) => {
              const filterType = filterModel.filterType || "text";
              const operationType = filterModel.type;
              const filterOutput: any = {
                field,
                operator: operationType,
                valueFrom: undefined,
                valueTo: undefined,
                value: undefined,
              };

              if (filterType === "number") {
                if (operationType === "inRange") {
                  filterOutput.valueFrom = filterModel.filter;
                  filterOutput.valueTo = filterModel.filterTo;
                } else if (
                  operationType === "blank" ||
                  operationType === "notBlank"
                ) {
                  filterOutput.value = undefined;
                } else {
                  filterOutput.value = filterModel.filter;
                }
              } else if (filterType === "date") {
                if (operationType === "inRange") {
                  filterOutput.valueFrom = filterModel.dateFrom;
                  filterOutput.valueTo = filterModel.dateTo;
                } else if (
                  operationType === "blank" ||
                  operationType === "notBlank"
                ) {
                  filterOutput.value = undefined;
                } else {
                  filterOutput.value = filterModel.dateFrom;
                }
              } else {
                filterOutput.value = filterModel.filter;
              }
              return filterOutput;
            }
          );
        };

        const convertSortModel = (sortModel: any[]) =>
          sortModel.map((sort) => ({
            field: sort.colId,
            direction: sort.sort.toUpperCase(),
          }));

        dispatch(
          fetchTls(false, {
            pagination: {
              page: Math.floor(params.startRow / pagination.pageSize) + 1,
              pageSize: params.endRow - params.startRow,
            },
            filters: [
              { field: "included", operator: "equals" as const, value: true },
              ...convertFilterModel(),
              ...(filter.selectedCategory
                ? [
                    {
                      field: "category",
                      operator: "equals" as const,
                      value: filter.selectedCategory,
                    },
                  ]
                : []),
              ...(filter.selectedAccount
                ? [
                    {
                      field: "account",
                      operator: "equals" as const,
                      value: filter.selectedAccount,
                    },
                  ]
                : []),
              ...(filter.tx1Account
                ? [
                    {
                      field: "tx1Account",
                      operator: "equals" as const,
                      value: filter.tx1Account,
                    },
                  ]
                : []),
              ...(filter.tx2Account
                ? [
                    {
                      field: "tx2Account",
                      operator: "equals" as const,
                      value: filter.tx2Account,
                    },
                  ]
                : []),
              ...(filter.linkFilterSource &&
              filter.linkFilterDest &&
              (filter.linkFilterSource === "Income"
                ? typeof filter.linkFilterDest === "string" &&
                  filter.linkFilterDest.startsWith("Income:")
                : typeof filter.linkFilterSource === "string" &&
                  filter.linkFilterSource.startsWith("Income:"))
                ? [{ field: "amount", operator: "greaterThan" as const, value: 0 }]
                : []),
              ...(filter.linkFilterSource &&
              filter.linkFilterDest &&
              (filter.linkFilterDest === "Expenses"
                ? typeof filter.linkFilterSource === "string" &&
                  filter.linkFilterSource.startsWith("Expense:")
                : typeof filter.linkFilterDest === "string" &&
                  filter.linkFilterDest.startsWith("Expense:"))
                ? [{ field: "amount", operator: "lessThan" as const, value: 0 }]
                : []),
            ],
            sorting: convertSortModel(params.sortModel || []),
            successCallback: params.successCallback,
            accountChunkLabels: latestForFetch.current.accountChunkLabels,
            documents: latestForFetch.current.documents,
            hasQbo: latestForFetch.current.hasQbo,
            job: latestForFetch.current.job,
            l1,
            l2,
          })
        );
      },
    };

    gridApiRef.current.setDatasource(dataSource);
  }, [
    dispatch,
    pagination.pageSize,
    selectedDocumentId,
    filter.selectedAccount,
    selectedChunk,
    refreshKey,
    isGridReady,
    filter.selectedCategory,
    filter.tx1Account,
    filter.tx2Account,
    filter.linkFilterSource,
    filter.linkFilterDest,
    l1,
    l2,
    gridApiRef,
  ]);

  const filterFn = useCallback(
    (rowNode: any) => {
      const source = filter.linkFilterSource;
      const destination = filter.linkFilterDest;
      if (!source || !destination) return true;

      const amount = parseFloat(rowNode.data?.amount);
      const rowCategory = norm(rowNode.data?.category);

      if (
        source === "Income" &&
        typeof destination === "string" &&
        destination.startsWith("Income:")
      ) {
        const categoryName =
          (destination.split(":")[1] || "").trim() || "Uncategorized";
        return (
          !matchedIds.includes(rowNode.data?.id) &&
          rowCategory === categoryName &&
          amount > 0
        );
      }

      if (
        destination === "Expenses" &&
        typeof source === "string" &&
        source.startsWith("Expense:")
      ) {
        const categoryName =
          (source.split(":")[1] || "").trim() || "Uncategorized";
        return (
          !matchedIds.includes(rowNode.data?.id) &&
          rowCategory === categoryName &&
          amount < 0
        );
      }

      if (data2.nodes.find((n) => n.name === source)?.category === "income") {
        const categoryFromSource =
          (String(source).split(":")[1] || "").trim() || "Uncategorized";
        return (
          !matchedIds.includes(rowNode.data?.id) &&
          rowNode.data?.account === destination &&
          amount > 0 &&
          rowCategory === categoryFromSource
        );
      }

      if (
        data2.nodes.find((n) => n.name === destination)?.category === "expense"
      ) {
        const categoryFromDest =
          (String(destination).split(":")[1] || "").trim() || "Uncategorized";
        return (
          !matchedIds.includes(rowNode.data?.id) &&
          rowNode.data?.account === source &&
          amount < 0 &&
          rowCategory === categoryFromDest
        );
      }

      if (matchedIds.includes(rowNode.data?.id)) {
        const match = matches.find((x) =>
          x.transactions.some((t) => t.id === rowNode.data?.id)
        );
        if (match) {
          const [tx1, tx2] = match.transactions;
          return (
            (tx1.account === source && tx2.account === destination) ||
            (tx1.account === destination && tx2.account === source)
          );
        }
      }

      return false;
    },
    [
      filter.linkFilterSource,
      filter.linkFilterDest,
      matchedIds,
      matches,
      data2.nodes,
    ]
  );

  const defaultColDef = useMemo(() => ({ resizable: true }), []);

  const onGridReady = useCallback(
    (params: any) => {
      gridApiRef.current = params.api;
      setIsGridReady(true);
      params.api.sizeColumnsToFit();
    },
    [gridApiRef]
  );

  const hasActiveFilter =
    filter.selectedAccount ||
    filter.selectedCategory ||
    filter.tx1Account ||
    filter.tx2Account ||
    (filter.linkFilterSource && filter.linkFilterDest);

  return (
    <>
      <div className="h3 mt-4">Transactions</div>
      <Row>
        <Col>
          Filter by
          <ExternalFilter
            columnDefs={externalFilterColumns}
            gridApi={gridApiRef.current}
          />
          <Row className="align-items-center">
            <div className="filter-info ml-3">
              {hasActiveFilter && <span>Filter: </span>}
              {filter.selectedAccount && filter.selectedCategory && (
                <span>
                  Account: {filter.selectedAccount}, Category:{" "}
                  {filter.selectedCategory}
                </span>
              )}
              {filter.selectedAccount &&
                !filter.selectedCategory &&
                !filter.linkFilterSource && (
                  <span>Account: {filter.selectedAccount}</span>
                )}
              {filter.selectedCategory &&
                !filter.selectedAccount &&
                !filter.linkFilterSource && (
                  <span>Category: {filter.selectedCategory}</span>
                )}
              {filter.linkFilterSource &&
                filter.linkFilterDest &&
                data2.nodes.find((n) => n.name === filter.linkFilterSource)
                  ?.category === "income" && (
                  <>
                    {(filter.selectedAccount || filter.selectedCategory) && (
                      <span className="ml-1">· </span>
                    )}
                    <span className="text-success font-weight-bold">
                      Income only (amount &gt; 0)
                    </span>
                  </>
                )}
              {filter.linkFilterSource &&
                filter.linkFilterDest &&
                data2.nodes.find((n) => n.name === filter.linkFilterDest)
                  ?.category === "expense" && (
                  <>
                    {(filter.selectedAccount || filter.selectedCategory) && (
                      <span className="ml-1">· </span>
                    )}
                    <span className="text-danger font-weight-bold">
                      Expense only (amount &lt; 0)
                    </span>
                  </>
                )}
              {filter.tx1Account && filter.tx2Account && (
                <span>
                  Transfer: {filter.tx1Account} ↔ {filter.tx2Account}
                </span>
              )}
            </div>
            {hasActiveFilter && (
              <Button variant="link" onClick={onClearFilters}>
                <FontAwesomeIcon icon="trash" />
                <span className="ml-1">Clear Filters</span>
              </Button>
            )}
          </Row>
        </Col>
      </Row>

      <Row>
        <div className="ag-theme-alpine" style={{ height: 600, width: "100%" }}>
          <AgGridReact
            ref={ref}
            singleClickEdit
            defaultColDef={defaultColDef}
            columnDefs={columnDefs}
            isExternalFilterPresent={() => true}
            doesExternalFilterPass={filterFn}
            pagination={true}
            paginationPageSize={pagination.pageSize}
            rowModelType={"infinite"}
            cacheBlockSize={pagination.pageSize}
            cacheOverflowSize={2}
            maxConcurrentDatasourceRequests={1}
            infiniteInitialRowCount={1}
            maxBlocksInCache={2}
            rowData={undefined}
            onGridReady={onGridReady}
          />
        </div>
      </Row>
    </>
  );
}
